/**
 * DetailGrid — the two-column detail body: edit sections + bookings on the
 * left, the rail on the right. Extracted from WorkshopDetailPage so each
 * component lives in its own file (react/no-multi-comp).
 */
import { Outlet, useNavigate } from 'react-router-dom';

import type { WorkshopDetail } from '../../../../api/workshops';
import { MOBILE_MAX, useMediaQuery } from '../../../../lib/useMediaQuery';

import { BookingsCard } from './BookingsCard';
import { acceptsCheckIn } from './checkInModel';
import { TeacherRoleNote } from './TeacherRoleNote';
import type { WorkshopEditForm } from './useWorkshopEditForm';
import { VenueLocationCard } from './VenueLocationCard';
import { useWorkshopViewMode, type CheckInRouteCtx } from './workshopDetailContext';
import { WorkshopDetailsCard } from './WorkshopDetailsCard';
import { WorkshopRightRail } from './WorkshopRightRail';

export function DetailGrid({
  workshop,
  form,
  readOnly,
  editable,
  showBookings,
}: {
  workshop: WorkshopDetail;
  form: WorkshopEditForm;
  readOnly: boolean;
  /** False for the Teacher persona, outside the viewer's region, or once cancelled. */
  editable: boolean;
  showBookings: boolean;
}) {
  const navigate = useNavigate();
  const viewMode = useWorkshopViewMode();
  // Below desktop the two columns stack into one flow (the rail drops beneath
  // the left column), matching how the detail surface behaves at those widths.
  const stacked = useMediaQuery(MOBILE_MAX);
  // Check-in needs an active (not cancelled) workshop. Editors get it; a
  // Teacher — though their record view is read-only — gets it too when they're
  // ASSIGNED crew (myRole, BON-239). RD read-only mode still gets no button.
  // The modal lives on the nested `checkin` route (BON-204 R2 deep-link).
  const teacherOnCrew = viewMode === 'teacher' && Boolean(workshop.myRole);
  const canCheckIn = (!readOnly || teacherOnCrew) && acceptsCheckIn(workshop.lifecycle);
  // Context for the nested checkin route + the close target (this detail page).
  // `..` steps from the nested `checkin` route back up to THIS detail route
  // (CheckInModalRoute's navigate/Navigate run from the child); `.` would resolve
  // to the checkin route itself, so the modal would never close / the guard would
  // redirect to itself.
  const ctx: CheckInRouteCtx = { workshop, canCheckIn, closeHref: '..' };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1.55fr 1fr',
        gap: 16,
        alignItems: 'start',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <TeacherRoleNote myRole={workshop.myRole} />
        <VenueLocationCard workshop={workshop} />
        <WorkshopDetailsCard workshop={workshop} form={form} editable={editable} />
        {showBookings && (
          <BookingsCard
            roster={workshop.roster}
            reservations={workshop.reservations}
            workshopId={workshop.id}
            treeReplacementPickups={workshop.treeReplacementPickups ?? []}
            canManageTreeReplacements={workshop.canManageTreeReplacements ?? false}
            venueTimeZone={workshop.venueTimeZone ?? null}
            pacing={{
              lifecycle: workshop.lifecycle,
              startsAt: workshop.startsAt,
              paceStatus: workshop.paceStatus,
            }}
            onOpenCheckIn={canCheckIn ? () => navigate('checkin') : undefined}
          />
        )}
      </div>
      <WorkshopRightRail workshop={workshop} readOnly={readOnly} />
      {/* The nested `checkin` route renders the modal here, over the grid. */}
      <Outlet context={ctx} />
    </div>
  );
}
